import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { getStateFromSessionStorage, setSessionStorageItem } from './helpers/sessionStorage';
import type { Config, Step, StepResponse } from './Config';

export interface OnboarderContextValue<TData> {
    data: TData;
    saveData: (data: TData) => void;
    config: Config<TData>;
    steps: Step<TData>[];
    stepsResponse: StepResponse[];
}

interface OnboarderContextProviderProps {
    children: React.ReactNode;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    config: Config<any>;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const OnboarderContext = createContext<OnboarderContextValue<any>>(null as any);

export function OnboarderContextProvider({ children, config }: OnboarderContextProviderProps) {
    const stateKey = `${config.storageKeyPrefix}_data`;
    const [data, setData] = useState({});
    const [showChild, setShowChild] = useState(false);

    useEffect(() => {
        const storedState = getStateFromSessionStorage(stateKey);

        if (storedState) {
            saveData(storedState);
        }
        setShowChild(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const saveData = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (data: any) => {
            setData(data);
            setSessionStorageItem(stateKey, JSON.stringify(data));
        },
        [stateKey, setData]
    );

    if (!showChild) return null;

    return (
        <OnboarderContext.Provider
            value={{
                data,
                saveData,
                config,
                steps: config.steps(data),
                stepsResponse: config.stepsResponse(data),
            }}
        >
            {children}
        </OnboarderContext.Provider>
    );
}

export function useOnboarder<Data>(): OnboarderContextValue<Data> {
    return useContext(OnboarderContext);
}
